import {AxiosInstance} from "axios";
import API, {BlobAndFilename, getBlobAndFileName} from "../../utils/api";
import {
    HealthCheckGroup,
    HealthCheckerCheck,
    HealthCheckerCheckRequest,
    HealthCheckerGroup,
    HealthCheckerGroupOption,
    HealthCheckerGroupRequest,
    HealthCheckerIncident,
    HealthCheckerIssue,
    HealthCheckerIssueDetails,
    HealthCheckerIssueResolveRequest,
} from "../../models/health-checker/health-checker";
import {PaginatedRequest} from "../../models/core/paginated.request";
import {PaginatedResult} from "../../models/core/paginated.result";

export class HealthCheckerService {
    private api: AxiosInstance = API;

    getChecksByGroups(): Promise<HealthCheckGroup[]> {
        return this.api
            .get<HealthCheckGroup[]>("health-checker/checks/by-groups")
            .then((response) => response.data);
    }

    getCheckers(): Promise<string[]> {
        return this.api
            .get<string[]>("health-checker/checks/checkers")
            .then((response) => response.data);
    }

    getAllGroups(): Promise<HealthCheckerGroupOption[]> {
        return this.api
            .get<HealthCheckerGroupOption[]>("health-checker/groups/all")
            .then((response) => response.data);
    }

    getChecks(request: PaginatedRequest): Promise<PaginatedResult<HealthCheckerCheck>> {
        return this.api
            .get<PaginatedResult<HealthCheckerCheck>>("health-checker/checks", {
                params: request.toParams(),
            })
            .then((response) => response.data);
    }

    createCheck(request: HealthCheckerCheckRequest): Promise<HealthCheckerCheck> {
        return this.api
            .post<HealthCheckerCheck>("health-checker/checks", request)
            .then((response) => response.data);
    }

    updateCheck(id: number, request: HealthCheckerCheckRequest): Promise<HealthCheckerCheck> {
        return this.api
            .post<HealthCheckerCheck>(`health-checker/checks/${id}`, request)
            .then((response) => response.data);
    }

    removeCheck(id: number): Promise<void> {
        return this.api
            .delete<void>(`health-checker/checks/${id}`)
            .then((response) => response.data);
    }

    getIssues(request: PaginatedRequest): Promise<PaginatedResult<HealthCheckerIssue>> {
        return this.api
            .get<PaginatedResult<HealthCheckerIssue>>("health-checker/issues", {
                params: request.toParams(),
            })
            .then((response) => response.data);
    }

    getIssue(id: number): Promise<HealthCheckerIssueDetails> {
        return this.api
            .get<HealthCheckerIssueDetails>(`health-checker/issues/${id}`)
            .then((response) => response.data);
    }

    resolveIssue(id: number, request: HealthCheckerIssueResolveRequest): Promise<void> {
        return this.api
            .post<void>(`health-checker/issues/${id}/resolve`, request)
            .then((response) => response.data);
    }

    getIssueIncidents(id: number, request: PaginatedRequest): Promise<PaginatedResult<HealthCheckerIncident>> {
        return this.api
            .get<PaginatedResult<HealthCheckerIncident>>(`health-checker/issues/${id}/incidents`, {
                params: request.toParams(),
            })
            .then((response) => response.data);
    }

    downloadIncidentData(id: number): Promise<BlobAndFilename> {
        return this.api
            .get(`health-checker/incidents/${id}/data`, {responseType: "blob"})
            .then(getBlobAndFileName);
    }

    getGroups(request: PaginatedRequest): Promise<PaginatedResult<HealthCheckerGroup>> {
        return this.api
            .get<PaginatedResult<HealthCheckerGroup>>("health-checker/groups", {
                params: request.toParams(),
            })
            .then((response) => response.data);
    }

    createGroup(request: HealthCheckerGroupRequest): Promise<HealthCheckerGroup> {
        return this.api
            .post<HealthCheckerGroup>("health-checker/groups", request)
            .then((response) => response.data);
    }

    updateGroup(id: number, request: HealthCheckerGroupRequest): Promise<HealthCheckerGroup> {
        return this.api
            .post<HealthCheckerGroup>(`health-checker/groups/${id}`, request)
            .then((response) => response.data);
    }

    removeGroup(id: number): Promise<void> {
        return this.api
            .delete<void>(`health-checker/groups/${id}`)
            .then((response) => response.data);
    }
}

export const healthCheckerService = new HealthCheckerService();
